import { z } from 'zod';
import { TypedApiClient } from '../index';
import { apiPath } from '../path';
import { idWindowPageSchema, type IdWindowPage, type IdWindowParams } from '../id-window';

// ====== Sub-schemas ======

export const TimeSchema = z.object({
  hour: z.number(),
  minute: z.number(),
}).partial().passthrough();

// The role snapshot as READ back (ATL-1954): stored whole, so further keys pass through, and a legacy
// snapshot may hold a null name/label.
export const RoleDetailsSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
  label: z.string().nullable(),
}).partial().passthrough();

// ====== Response Schema ======

export const WorkingHoursResponseSchema = z.object({
  id: z.string(),
  firestore_id: z.string().optional().nullable(),
  organization_id: z.string().optional().nullable(),
  store_id: z.string().optional().nullable(),
  employee_id: z.string().optional().nullable(),
  date: z.string().optional().nullable(),
  start_date: z.string().optional().nullable(),
  end_date: z.string().optional().nullable(),
  start_hour: TimeSchema.optional().nullable(),
  end_hour: TimeSchema.optional().nullable(),
  role: z.string().optional().nullable(),
  role_details: RoleDetailsSchema.optional().nullable(),
  salary_type: z.string().optional().nullable(),
  amount: z.number().optional().nullable(),
  employee_bonus_amount: z.number().optional().nullable(),
  daily_stat_id: z.string().optional().nullable(),
  monthly_stat_id: z.string().optional().nullable(),
  created_at: z.string().optional().nullable(),
}).passthrough();

export const BulkWorkingHoursResponseSchema = z.object({
  message: z.string(),
  count: z.number(),
  data: z.array(WorkingHoursResponseSchema),
}).passthrough();

// ====== Create/Update Schemas ======

export const CreateWorkingHoursSchema = z.object({
  storeId: z.string().optional(),
  employeeId: z.string(),
  role: z.string().optional(),
  // ATL-1954 — the WHOLE role snapshot, any object (exactly the backend's `@IsObject()`, the generated
  // `CreateWorkingHoursDto`). Any stricter shape here refuses, client-side, a snapshot the backend
  // would accept — and that is a deterministic dual-write failure that loses the record.
  roleDetails: z.object({}).passthrough().optional(),
  salaryType: z.enum(['hour', 'day', 'month']).optional(),
  amount: z.number().optional(),
  date: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  startHour: TimeSchema.optional(),
  endHour: TimeSchema.optional(),
  employeeBonusAmount: z.number().optional(),
  // ATL-1954 — Firestore's dailyStatID / monthlyStatID; the backend persists them since this ticket.
  dailyStatId: z.string().optional(),
  monthlyStatId: z.string().optional(),
}).passthrough();

export const UpdateWorkingHoursSchema = CreateWorkingHoursSchema.partial();

// ====== Type Exports ======

export type WorkingHoursResponse = z.infer<typeof WorkingHoursResponseSchema>;
export type CreateWorkingHours = z.infer<typeof CreateWorkingHoursSchema>;
export type UpdateWorkingHours = z.infer<typeof UpdateWorkingHoursSchema>;
export type BulkWorkingHoursResponse = z.infer<typeof BulkWorkingHoursResponseSchema>;

/**
 * Create working hours API factory
 */
export function createWorkingHoursApi(client: TypedApiClient) {
  return {
    /**
     * Get working hours with optional filters
     */
    getWorkingHours: (params?: {
      storeId?: string;
      employeeId?: string;
      startDate?: string;
      endDate?: string;
      limit?: number;
      offset?: number;
    }): Promise<WorkingHoursResponse[]> => {
      return client.get('/working-hours', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(WorkingHoursResponseSchema),
      });
    },

    /**
     * ATL-1477: one snapshot window of `/working-hours` — see `../id-window.ts`. Use it through
     * `readWholeList`, never directly: a single window is not the list.
     */
    getWorkingHoursWindow: (
      params: {
      storeId?: string;
      employeeId?: string;
      startDate?: string;
      endDate?: string;
      } & IdWindowParams,
    ): Promise<IdWindowPage<z.infer<typeof WorkingHoursResponseSchema>>> => {
      return client.get('/working-hours/window', {
        params: params as Record<string, unknown>,
        responseSchema: idWindowPageSchema(WorkingHoursResponseSchema),
      });
    },

    /**
     * Get a single working hours record by ID
     */
    getWorkingHoursById: (id: string): Promise<WorkingHoursResponse> => {
      return client.get(apiPath`/working-hours/${id}`, {
        responseSchema: WorkingHoursResponseSchema,
      });
    },

    /**
     * Create a new working hours record
     */
    createWorkingHours: (data: CreateWorkingHours): Promise<WorkingHoursResponse> => {
      return client.post('/working-hours', {
        data,
        requestSchema: CreateWorkingHoursSchema,
        responseSchema: WorkingHoursResponseSchema,
      });
    },

    /**
     * Update an existing working hours record
     */
    updateWorkingHours: (id: string, data: UpdateWorkingHours): Promise<WorkingHoursResponse> => {
      return client.patch(apiPath`/working-hours/${id}`, {
        data,
        requestSchema: UpdateWorkingHoursSchema,
        responseSchema: WorkingHoursResponseSchema,
      });
    },

    /**
     * Delete a working hours record
     */
    deleteWorkingHours: (id: string) => {
      return client.delete(apiPath`/working-hours/${id}`, {
        responseSchema: z.object({ message: z.string() }),
      });
    },

    /**
     * Update a working hours record by Firestore ID
     */
    updateWorkingHoursByFirestoreId: (firestoreId: string, data: UpdateWorkingHours): Promise<WorkingHoursResponse> => {
      return client.patch(apiPath`/working-hours/firestore/${firestoreId}`, {
        data,
        requestSchema: UpdateWorkingHoursSchema,
        responseSchema: WorkingHoursResponseSchema,
      });
    },

    /**
     * Delete a working hours record by Firestore ID
     */
    deleteWorkingHoursByFirestoreId: (firestoreId: string) => {
      return client.delete(apiPath`/working-hours/firestore/${firestoreId}`, {
        responseSchema: z.object({ success: z.boolean() }),
      });
    },
  };
}
